import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #swipeController = null;

  /** @type {{ id: number, x: number, y: number, started: number, horizontal: boolean } | null} */
  #swipeGesture = null;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#swipeController = new AbortController();
    const signal = this.#swipeController.signal;
    this.#dialog?.addEventListener('touchstart', this.#handleSwipeStart, { passive: true, signal });
    this.#dialog?.addEventListener('touchmove', this.#handleSwipeMove, { passive: false, signal });
    this.#dialog?.addEventListener('touchend', this.#handleSwipeEnd, { passive: true, signal });
    this.#dialog?.addEventListener('touchcancel', this.#cancelSwipe, { passive: true, signal });

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#swipeController?.abort();
    this.#swipeController = null;
    this.#swipeGesture = null;
  }

  /** @param {TouchEvent} event */
  #handleSwipeStart = (event) => {
    this.#swipeGesture = null;
    const dialog = this.#dialog;
    const target = event.target instanceof Element ? event.target : null;
    if (!dialog || !target || !this.#themeDrawer?.isOpen || event.touches.length !== 1) return;
    if (target.closest('dialog') !== dialog || dialog.querySelector('dialog[open]')) return;
    // Preserve links, form controls and the entire horizontal accessory carousel.
    if (target.closest('button, a, input, textarea, select, summary, [contenteditable], cart-upsells, .cart-upsells')) return;
    const touch = event.touches[0];
    const distanceFromLeft = touch.clientX - dialog.getBoundingClientRect().left;
    if (distanceFromLeft < 0 || distanceFromLeft > 64) return;
    this.#swipeGesture = { id: touch.identifier, x: touch.clientX, y: touch.clientY, started: event.timeStamp, horizontal: false };
  };

  /** @param {TouchEvent} event */
  #handleSwipeMove = (event) => {
    const gesture = this.#swipeGesture;
    if (!gesture) return;
    if (event.touches.length !== 1 || !this.#themeDrawer?.isOpen) return this.#cancelSwipe();
    const touch = Array.from(event.touches).find((item) => item.identifier === gesture.id);
    if (!touch) return this.#cancelSwipe();
    const dx = touch.clientX - gesture.x;
    const dy = Math.abs(touch.clientY - gesture.y);
    if (!gesture.horizontal) {
      if (dx < -12 || (dy > 12 && dy >= Math.abs(dx))) return this.#cancelSwipe();
      if (dx > 12 && dx > dy * 1.5) gesture.horizontal = true;
    }
    // Only claim an established rightward gesture; vertical scrolling stays native.
    if (gesture.horizontal && event.cancelable) event.preventDefault();
  };

  /** @param {TouchEvent} event */
  #handleSwipeEnd = (event) => {
    const gesture = this.#swipeGesture;
    this.#swipeGesture = null;
    if (!gesture || event.touches.length || !this.#themeDrawer?.isOpen) return;
    const touch = Array.from(event.changedTouches).find((item) => item.identifier === gesture.id);
    if (!touch) return;
    const dx = touch.clientX - gesture.x;
    const dy = Math.abs(touch.clientY - gesture.y);
    if (dx >= 80 && dx > dy * 1.5 && event.timeStamp - gesture.started <= 1200) {
      this.#themeDrawer?.close();
    }
  };

  #cancelSwipe = () => {
    this.#swipeGesture = null;
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#swipeGesture = null;
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
